import { Link } from "react-router-dom";
import { Ladybug } from "@/components/site/Ladybug";
import { PageIntro } from "@/components/site/PageIntro";
import { Syllabus } from "@/components/site/Syllabus";

const Cohort = ({ n, title, tag, children, testid }) => (
  <article data-testid={testid} className="card-soft p-8 sm:p-12 hover:border-[#b6689a]">
    <span className="eyebrow">Cohort {n}</span>
    <h2 className="font-serif-h text-3xl sm:text-4xl text-[#577731] mt-4">{title}</h2>
    {tag && <p className="mt-4 text-xs sm:text-sm tracking-[0.2em] uppercase font-semibold text-[#b6689a]">{tag}</p>}
    <div className="mt-6 space-y-5 text-[#232b1b]/80 leading-relaxed">{children}</div>
  </article>
);

export default function University() {
  return (
    <div data-testid="university-page">
      <PageIntro eyebrow="The Healers University" title="Where the Called Become the Healers" testid="university-title">
        <p>
          Some are called to receive the medicine. Others are called to carry it. If you have felt that stirring, the pull to serve, to build, to heal, the University is where that calling becomes your craft.
        </p>
        <div className="pt-4 flex flex-wrap gap-4">
          <a href="https://teachclub.com/app/learn/6ab72c9a98dca530e6f77b0b/lesson/36d9b508-8711-42f2-a236-0bc9fd6fb526" target="_blank" rel="noreferrer" data-testid="university-syllabus-link" className="btn btn-mauve">
            View the Holistic Health Care Practitioner Certification Syllabus <Ladybug size={16} rotate={25} />
          </a>
          <a href="https://teachclub.com/app/learn/6ab72c9a98dca530e6f77b0b/lesson/45fbc5f0-7ea9-41ce-add5-3b85cf15b86e" target="_blank" rel="noreferrer" data-testid="university-syllabus-link-2" className="btn btn-ghost">
            Syllabus: Part Two
          </a>
        </div>
      </PageIntro>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 hidden lg:block">
          <div className="sticky top-28 relative">
            <img src="/img/university.jpg" alt="A student reading among the plants" className="arch aspect-[3/5] object-cover w-full" />
            <Ladybug spot="bl" size={26} rotate={20} />
          </div>
        </div>
        <div className="lg:col-span-8 space-y-8">
          <Cohort n="One" title="The Cosmic Business Blueprint" tag="Make money. Be prosperous now. Make impact now." testid="cohort-cosmic">
            <p className="text-sm font-semibold text-[#577731]" data-testid="cosmic-duration">A 9 to 13 week cohort</p>
            <p className="font-serif-h italic text-2xl text-[#232b1b]">Your business is not separate from your spirit. It is a vessel for it.</p>
            <p>The Cosmic Business Blueprint is a spiritual business and banking system for entrepreneurs ready to build wealth on sacred ground. Whether you are launching for the first time or relaunching what you have already built, you will rise from this cohort with your business stronger, clearer, and more aligned than ever.</p>
            <p>Over 9 to 13 weeks, you will build a real business system and strategy that puts you first, not one that drains you dry. Through ancestral practice, astrological mapping, ritual, and real-world structure, you will align your money with your mission and open the roads so prosperity flows through everything you build.</p>
            <p className="font-medium text-[#232b1b]">This is how you call in:</p>
            <ul className="space-y-2" data-testid="cosmic-call-in-list">
              {["Having a solid foundation and understanding of you and your business", "More prosperity", "More abundance", "More rest", "More money: big money and tall money", "Fast money", "Long money", "Big money", "Money with a mission"].map((t) => (
                <li key={t} className="flex gap-3"><span className="text-[#b6689a]">✦</span>{t}</li>
              ))}
              <li className="flex gap-3 rounded-2xl bg-[#b6689a]/10 px-4 py-3 font-serif-h text-xl text-[#232b1b]" data-testid="cosmic-legacy-item">
                <span className="text-[#b6689a]">✦</span><span>And most importantly: <strong className="text-[#577731]">generational wealth &amp; legacy building</strong></span>
              </li>
            </ul>
            <p>Stop waiting for permission to prosper. The blueprint is ready.</p>
          </Cohort>
          <Cohort n="Two" title="Holistic Home Care Practitioner Apprenticeship" tag="Begins Fall/Winter 2026" testid="cohort-apprenticeship">
            <p>The old ways were taught in kitchens, at bedsides, and in the presence of elders. This apprenticeship honors that lineage.</p>
            <p>Learn the art of holistic home care hands-on, walking beside experienced healers, and step into your role as a practitioner who brings the medicine home to the families who need it.</p>
            <div className="rounded-3xl bg-[#b1cc90]/20 p-6 sm:p-8 space-y-4" data-testid="apprenticeship-journey">
              <p className="font-serif-h text-2xl text-[#577731]">A two-year journey of transformation. <Ladybug size={20} rotate={-60} /></p>
              <p>This is not a weekend workshop. It is a full apprenticeship that walks you from your own healing into your calling as a practitioner. Over two years, you will move through:</p>
              <ul className="space-y-3">
                {[
                  ["Uncrossing Yourself & Your Home", "clearing your own path and your own house before you tend to anyone else's"],
                  ["Medicine Making", "crafting remedies, oils, tinctures, and sacred preparations by hand"],
                  ["Apothecary Building", "creating a working apothecary you can serve your family and community from"],
                  ["Birthwork", "holding mothers and families through the sacred passage of birth"],
                  ["Certification in Holistic Health Care", "graduating as a certified holistic home care practitioner"],
                ].map(([h, d]) => (
                  <li key={h} className="flex gap-3"><span className="text-[#b6689a]">✦</span><span><strong className="text-[#232b1b]">{h}:</strong> {d}</span></li>
                ))}
              </ul>
              <p className="font-serif-h italic text-xl text-[#232b1b]">You come in seeking. You leave carrying the medicine.</p>
            </div>
          </Cohort>
          <Syllabus />
          <Link to="/apply?program=cosmic,apprenticeship" data-testid="university-apply-btn" className="btn btn-olive">Enroll Now</Link>
        </div>
      </section>
    </div>
  );
}
